/**
 * Call disposition picker and catalog editor.
 * Props follow the follow-ups module: api is API.json, toast(message, type).
 * DispositionPicker also takes callSid, timezone, compact, and onSaved.
 */
const { useState, useEffect } = React;

const GROUPS = [
  ['positive', 'Positive'],
  ['neutral', 'Neutral'],
  ['negative', 'Negative'],
  ['dnc', 'Do not call']
];
const STATUS_OPTIONS = [
  ['', 'No change'],
  ['new', 'New'],
  ['interested', 'Interested'],
  ['not_interested', 'Not interested'],
  ['dnc', 'Do not call']
];

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function safeZone(timeZone) {
  const zone = timeZone || 'America/Los_Angeles';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date());
    return zone;
  } catch {
    return 'UTC';
  }
}

function zoneParts(date, timeZone) {
  const map = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  let hour = Number(map.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour,
    minute: Number(map.minute),
    second: Number(map.second)
  };
}

function zoneOffsetMs(utcMs, timeZone) {
  const parts = zoneParts(new Date(utcMs), timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - utcMs;
}

function wallTimeToUtc(localValue, timeZone) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(localValue || ''));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const zone = safeZone(timeZone);
  const wanted = Date.UTC(year, month - 1, day, hour, minute, 0);
  let utc = wanted;
  for (let i = 0; i < 3; i += 1) {
    const next = wanted - zoneOffsetMs(utc, zone);
    if (next === utc) break;
    utc = next;
  }
  return new Date(utc).toISOString();
}

function utcToWallInput(iso, timeZone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = zoneParts(date, safeZone(timeZone));
  const pad = (n) => String(n).padStart(2, '0');
  return parts.year + '-' + pad(parts.month) + '-' + pad(parts.day) + 'T' + pad(parts.hour) + ':' + pad(parts.minute);
}

function formatSetAt(iso, timeZone) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(timeZone),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).formatToParts(date);
  const pick = (type) => {
    const part = parts.find((item) => item.type === type);
    return part ? part.value : '';
  };
  const month = pick('month');
  const day = pick('day');
  const hour = pick('hour');
  const minute = pick('minute');
  if (!month || !day || !hour || !minute) return '';
  const period = pick('dayPeriod');
  const tzName = pick('timeZoneName');
  return month + ' ' + day + ', ' + hour + ':' + minute + (period ? ' ' + period : '') + (tzName ? ' ' + tzName : '');
}

function whoWhen(disposition, timeZone) {
  if (!disposition) return '';
  const when = formatSetAt(disposition.created_at, timeZone);
  const who = disposition.author_name ? 'Set by ' + disposition.author_name : 'Set';
  return when ? who + ' · ' + when : who;
}

function slugify(label) {
  let code = String(label || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 64);
  if (!code) return '';
  if (!/^[a-z]/.test(code)) code = ('d_' + code).slice(0, 64);
  return code;
}

function tone(category) {
  return category === 'positive' || category === 'negative' || category === 'dnc' ? category : 'neutral';
}

export function DispositionBadge({ code, label, category }) {
  const text = label || code;
  if (!text) return null;
  return <span className={'disp-badge disp-badge--' + tone(category)}>{text}</span>;
}

export function DispositionPicker({ api, toast, callSid, timezone, compact, onSaved }) {
  const zone = safeZone(timezone);
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState('');
  const [note, setNote] = useState('');
  const [whenLocal, setWhenLocal] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  function notify(message, type) { if (toast) toast(message, type); }

  useEffect(() => {
    if (!callSid) {
      setLoading(false);
      setItems([]);
      setCurrent(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      api('GET', '/api/dispositions'),
      api('GET', '/api/calls/' + encodeURIComponent(callSid) + '/disposition')
    ]).then(([catalog, saved]) => {
      if (cancelled) return;
      const rows = (catalog && catalog.items) || [];
      const disposition = saved && saved.disposition;
      const stillActive = disposition && rows.some((row) => row.code === disposition.code && row.is_active !== false);
      setItems(rows);
      setCurrent(disposition || null);
      setSelected(stillActive ? disposition.code : '');
      setNote(disposition && disposition.note ? disposition.note : '');
      setWhenLocal(disposition && disposition.follow_up_at ? utcToWallInput(disposition.follow_up_at, zone) : '');
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err, 'Could not load dispositions.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [callSid, tick, zone]);

  const active = items.filter((item) => item.is_active !== false);
  const chosen = items.find((item) => item.code === selected) || null;
  const needsNote = !!(chosen && chosen.requires_note);
  const needsTime = !!(chosen && chosen.creates_follow_up);

  async function save(event) {
    event.preventDefault();
    if (!callSid || !chosen || busy) return;
    if (needsNote && !note.trim()) {
      notify('Add a note for this disposition.', 'error');
      return;
    }
    const body = { code: chosen.code };
    if (note.trim()) body.note = note.trim();
    if (needsTime) {
      const followUpAt = wallTimeToUtc(whenLocal, zone);
      if (!followUpAt) {
        notify('Enter a follow-up date and time.', 'error');
        return;
      }
      body.follow_up_at = followUpAt;
    }
    setBusy(true);
    try {
      const saved = await api('POST', '/api/calls/' + encodeURIComponent(callSid) + '/disposition', body);
      setCurrent(saved && saved.disposition ? saved.disposition : null);
      notify(saved && saved.follow_up ? 'Disposition saved. Follow-up added.' : 'Disposition saved.', 'success');
      if (onSaved) onSaved(saved);
    } catch (err) {
      notify(errorMessage(err, 'Could not save this disposition.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!callSid) {
    return <div className={'disp' + (compact ? ' disp--compact' : '')}><p className="disp-empty">Open a call to set a disposition.</p></div>;
  }

  return (
    <form className={'disp' + (compact ? ' disp--compact' : '')} onSubmit={save}>
      <div>
        <h3 className="disp-title">Disposition</h3>
        {compact ? null : <p className="disp-help">Times use {zone}.</p>}
      </div>
      {error ? (
        <div className="disp-error" role="alert">
          {error}
          <div className="disp-actions">
            <button type="button" className="disp-btn" onClick={() => setTick((n) => n + 1)}>Try again</button>
          </div>
        </div>
      ) : null}
      <div className="disp-current">
        {current ? <DispositionBadge code={current.code} label={current.label} category={current.category} /> : <span className="disp-help">No disposition yet.</span>}
        {current ? <span className="disp-meta">{whoWhen(current, zone)}</span> : null}
      </div>
      {loading ? <p className="disp-help">Loading dispositions…</p> : null}
      {!loading && !active.length ? <p className="disp-empty">No dispositions are turned on. An admin can add them in settings.</p> : null}
      <div className="disp-groups">
        {GROUPS.map(([category, label]) => {
          const group = active.filter((item) => (item.category || 'neutral') === category);
          if (!group.length) return null;
          return (
            <div key={category} role="group" aria-label={label}>
              <p className="disp-group-label">{label}</p>
              <div className="disp-grid">
                {group.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className={'disp-choice disp-choice--' + tone(item.category) + (selected === item.code ? ' disp-choice--selected' : '')}
                    aria-pressed={selected === item.code}
                    disabled={busy}
                    onClick={() => setSelected(item.code)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="disp-field">
        <label htmlFor={'disp-note-' + callSid}>{needsNote ? 'Note (required)' : 'Note'}</label>
        <textarea
          id={'disp-note-' + callSid}
          className="disp-note"
          rows={compact ? 2 : 3}
          maxLength={2000}
          value={note}
          disabled={busy}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      {needsTime ? (
        <div className="disp-field">
          <label htmlFor={'disp-when-' + callSid}>Follow-up</label>
          <input
            id={'disp-when-' + callSid}
            className="disp-input"
            type="datetime-local"
            required
            value={whenLocal}
            disabled={busy}
            onChange={(event) => setWhenLocal(event.target.value)}
          />
          <span className="disp-help">Interpreted in {zone}, then saved in UTC.</span>
        </div>
      ) : null}
      <div className="disp-actions">
        <button type="submit" className="disp-btn disp-btn--primary" disabled={busy || !chosen || loading}>
          {busy ? 'Saving…' : 'Save disposition'}
        </button>
      </div>
    </form>
  );
}

function blankRow(sortOrder) {
  return {
    code: '',
    label: '',
    category: 'neutral',
    sets_lead_status: '',
    creates_follow_up: false,
    requires_note: false,
    sort_order: sortOrder,
    is_active: true,
    is_new: true,
    code_touched: false
  };
}

export function DispositionSettings({ api, toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function notify(message, type) { if (toast) toast(message, type); }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/dispositions')
      .then((result) => {
        if (cancelled) return;
        const rows = ((result && result.items) || []).map((row) => ({
          code: row.code,
          label: row.label,
          category: row.category,
          sets_lead_status: row.sets_lead_status || '',
          creates_follow_up: !!row.creates_follow_up,
          requires_note: !!row.requires_note,
          sort_order: row.sort_order,
          is_active: row.is_active !== false,
          is_new: false,
          code_touched: true
        }));
        setItems(rows);
      })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load dispositions.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function update(index, patch) {
    setItems((rows) => rows.map((row, i) => (i === index ? Object.assign({}, row, patch) : row)));
  }

  function move(index, delta) {
    setItems((rows) => {
      const next = rows.slice();
      const target = index + delta;
      if (target < 0 || target >= next.length) return rows;
      const moved = next.splice(index, 1)[0];
      next.splice(target, 0, moved);
      return next.map((row, i) => Object.assign({}, row, { sort_order: (i + 1) * 10 }));
    });
  }

  async function save(event) {
    event.preventDefault();
    if (busy) return;
    const used = new Set();
    const payload = [];
    for (const row of items) {
      const label = String(row.label || '').trim();
      if (!label) {
        notify('Each disposition needs a name.', 'error');
        return;
      }
      let code = String(row.code || '').trim().toLowerCase() || slugify(label);
      if (!code || !/^[a-z][a-z0-9_]{0,63}$/.test(code)) {
        notify('Codes use lowercase letters, numbers, and underscores.', 'error');
        return;
      }
      if (used.has(code)) {
        notify('Each disposition code can only appear once.', 'error');
        return;
      }
      used.add(code);
      payload.push({
        code,
        label,
        category: row.category,
        sets_lead_status: row.sets_lead_status || null,
        creates_follow_up: !!row.creates_follow_up,
        requires_note: !!row.requires_note,
        sort_order: payload.length * 10 + 10,
        is_active: !!row.is_active
      });
    }
    setBusy(true);
    try {
      const saved = await api('PUT', '/api/dispositions', { items: payload });
      const rows = ((saved && saved.items) || []).map((row) => ({
        code: row.code,
        label: row.label,
        category: row.category,
        sets_lead_status: row.sets_lead_status || '',
        creates_follow_up: !!row.creates_follow_up,
        requires_note: !!row.requires_note,
        sort_order: row.sort_order,
        is_active: row.is_active !== false,
        is_new: false,
        code_touched: true
      }));
      setItems(rows);
      const retained = saved && saved.retained;
      notify(retained && retained.length
        ? 'Dispositions saved. Outcomes already used on calls were turned off instead of removed.'
        : 'Dispositions saved.', 'success');
    } catch (err) {
      notify(errorMessage(err, 'Could not save dispositions.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="disp disp-settings" onSubmit={save}>
      <div>
        <h3 className="disp-title">Call dispositions</h3>
        <p className="disp-help">
          Outcomes a person can set after a call. Turning one off keeps it on past calls.
          Lead status is New, Interested, Not interested, or Do not call. Booked meeting uses Interested, because this workspace has no separate booked status.
        </p>
      </div>
      {error ? <div className="disp-error" role="alert">{error}</div> : null}
      {loading ? <p className="disp-help">Loading dispositions…</p> : null}
      <div className="disp-settings-head" aria-hidden="true">
        <span />
        <span>Name</span>
        <span>Category</span>
        <span>Lead status</span>
        <span>Follow-up</span>
        <span>Active</span>
      </div>
      {items.map((row, index) => (
        <div className="disp-row" key={row.code || 'new-' + index}>
          <div className="disp-order">
            <button type="button" className="disp-btn" aria-label={'Move ' + (row.label || 'disposition') + ' up'} disabled={busy || index === 0} onClick={() => move(index, -1)}>Up</button>
            <button type="button" className="disp-btn" aria-label={'Move ' + (row.label || 'disposition') + ' down'} disabled={busy || index === items.length - 1} onClick={() => move(index, 1)}>Down</button>
          </div>
          <div className="disp-field">
            <label className="disp-code" htmlFor={'disp-label-' + index}>Name</label>
            <input
              id={'disp-label-' + index}
              className="disp-input"
              maxLength={80}
              value={row.label}
              disabled={busy}
              onChange={(event) => {
                const label = event.target.value;
                const patch = { label };
                if (row.is_new && !row.code_touched) patch.code = slugify(label);
                update(index, patch);
              }}
            />
            {row.is_new ? (
              <input
                className="disp-input"
                aria-label="Code"
                value={row.code}
                disabled={busy}
                onChange={(event) => update(index, { code: event.target.value.toLowerCase(), code_touched: true })}
              />
            ) : <span className="disp-code">{row.code}</span>}
          </div>
          <div className="disp-field">
            <label className="disp-code" htmlFor={'disp-cat-' + index}>Category</label>
            <select
              id={'disp-cat-' + index}
              className="disp-select"
              value={row.category}
              disabled={busy}
              onChange={(event) => {
                const category = event.target.value;
                const patch = { category };
                if (category === 'dnc') patch.sets_lead_status = 'dnc';
                update(index, patch);
              }}
            >
              {GROUPS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="disp-field">
            <label className="disp-code" htmlFor={'disp-status-' + index}>Lead status</label>
            <select
              id={'disp-status-' + index}
              className="disp-select"
              value={row.sets_lead_status || ''}
              disabled={busy}
              onChange={(event) => update(index, { sets_lead_status: event.target.value })}
            >
              {STATUS_OPTIONS.map(([value, label]) => <option key={value || 'none'} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="disp-checks">
            <label className="disp-check">
              <input type="checkbox" checked={!!row.creates_follow_up} disabled={busy} onChange={(event) => update(index, { creates_follow_up: event.target.checked })} />
              Follow-up
            </label>
            <label className="disp-check">
              <input type="checkbox" checked={!!row.requires_note} disabled={busy} onChange={(event) => update(index, { requires_note: event.target.checked })} />
              Note
            </label>
          </div>
          <label className="disp-check">
            <input type="checkbox" checked={row.is_active !== false} disabled={busy} onChange={(event) => update(index, { is_active: event.target.checked })} />
            Active
          </label>
        </div>
      ))}
      <div className="disp-actions">
        <button type="button" className="disp-btn" disabled={busy || loading} onClick={() => setItems((rows) => rows.concat([blankRow((rows.length + 1) * 10)]))}>Add disposition</button>
        <button type="submit" className="disp-btn disp-btn--primary" disabled={busy || loading}>{busy ? 'Saving…' : 'Save dispositions'}</button>
      </div>
    </form>
  );
}
